// Master-product EAV attributes, flattened into display chips for the inquiry
// item wizard's variant picker.
//
// The picker is a COMPARISON table: the operator is deciding between variants
// of one family that often differ on nothing else visible. So chip order must
// be identical on every row — if one row reads "Grade A · Bone-in" and the next
// "Bone-in · Grade B", the eye can't scan the column and the operator is back
// to guessing. Types are therefore ordered by the dictionary's sortOrder (the
// order the dictionary admin chose), not by whatever key order the API happened
// to serialize, and values are sorted within a type.
//
// Chips carry the dictionary LABEL ("Chump Off"), not the stored slug
// ("chump-off"); a value the dictionary doesn't know falls back to the raw
// value, so legacy rows still read sensibly.

import { groupAttributesByType, type MasterProductAttribute } from 'apis/master-products';
import { dictionaryLabelFor, type AttributeDictionaryType } from 'apis/product-attributes';

export interface AttributeChip {
  type: string;
  value: string;
  label: string;
}

// Types absent from the dictionary sort last (Infinity) and then
// alphabetically, so their order is at least stable across rows too.
const typeRank = (dictionary: AttributeDictionaryType[] | undefined, type: string): number => {
  const found = dictionary?.find((entry) => entry.key === type);
  return found ? found.sortOrder : Number.POSITIVE_INFINITY;
};

export function attributeChips(
  attributes: MasterProductAttribute[] | undefined,
  dictionary: AttributeDictionaryType[] | undefined
): AttributeChip[] {
  const grouped = groupAttributesByType(attributes);

  return Object.keys(grouped)
    .sort((a, b) => typeRank(dictionary, a) - typeRank(dictionary, b) || a.localeCompare(b))
    .flatMap((type) => {
      const labelFor = dictionaryLabelFor(dictionary, type);
      return [...grouped[type]]
        .sort((a, b) => a.localeCompare(b))
        .map((value) => ({ type, value, label: labelFor(value) }));
    });
}
